import type {
  Cadeia,
  Caractere,
  Celula,
  IdPalavra,
  Orientacao,
  PalavraCanonica,
  PalavraColocada,
  Regiao,
} from '../tipos';
import { caracteresCanonicos, chaveCelula } from '../lexico/normalizacao';
import { celulaNoIndice, regiaoDeCelulas, unirRegioes, REGIAO_VAZIA } from './coordenadas';

/**
 * Índice espacial das palavras confirmadas.
 *
 * A grade é derivada da cadeia — nunca é fonte de verdade. Isso mantém desfazer
 * (§11) e restauração (§12) triviais: basta recompor a partir da lista de
 * palavras, sem estado espacial a migrar.
 */

export interface OcupacaoCelula {
  readonly caractere: Caractere;
  /** Palavras que ocupam esta célula: no máximo uma por orientação (§9). */
  readonly palavras: readonly IdPalavra[];
  /** Orientações já presentes aqui. É o que impede duas palavras colineares. */
  readonly orientacoes: readonly Orientacao[];
}

export interface Grade {
  readonly regiao: Regiao;
  readonly totalCelulas: number;
  ocupada(c: Celula): boolean;
  em(c: Celula): OcupacaoCelula | undefined;
  caractereEm(c: Celula): Caractere | undefined;
  /** Ids das palavras que passam por uma célula. */
  palavrasEm(c: Celula): readonly IdPalavra[];
  /**
   * Já existe palavra nesta célula correndo nesta direção?
   *
   * Uma palavra pode cruzar outra (§9, spec 1.2), mas não pode andar por cima
   * dela: `CASA` e `CASACA` na mesma linha compartilhariam quatro células e o
   * respiro das pontas não pegaria isso.
   */
  temOrientacao(c: Celula, orientacao: Orientacao): boolean;
}

export const GRADE_VAZIA: Grade = {
  regiao: REGIAO_VAZIA,
  totalCelulas: 0,
  ocupada: () => false,
  em: () => undefined,
  caractereEm: () => undefined,
  palavrasEm: () => [],
  temOrientacao: () => false,
};

export function construirGrade(cadeia: Cadeia): Grade {
  const mapa = new Map<
    string,
    { caractere: Caractere; palavras: IdPalavra[]; orientacoes: Orientacao[] }
  >();
  let regiao: Regiao | undefined;

  for (const palavra of cadeia.palavras) {
    const letras = caracteresCanonicos(palavra.texto);
    const celulas: Celula[] = [];

    for (let i = 0; i < letras.length; i += 1) {
      const letra = letras[i];
      if (letra === undefined) continue;

      const c = celulaNoIndice(palavra.origem, palavra.orientacao, i);
      celulas.push(c);

      const k = chaveCelula(c.coluna, c.linha);
      const existente = mapa.get(k);
      if (existente === undefined) {
        mapa.set(k, {
          caractere: letra,
          palavras: [palavra.id],
          orientacoes: [palavra.orientacao],
        });
      } else {
        existente.palavras.push(palavra.id);
        if (!existente.orientacoes.includes(palavra.orientacao)) {
          existente.orientacoes.push(palavra.orientacao);
        }
      }
    }

    const r = regiaoDeCelulas(celulas);
    regiao = regiao === undefined ? r : unirRegioes(regiao, r);
  }

  const regiaoFinal = regiao ?? REGIAO_VAZIA;

  return {
    regiao: regiaoFinal,
    totalCelulas: mapa.size,
    ocupada: (c) => mapa.has(chaveCelula(c.coluna, c.linha)),
    em: (c) => mapa.get(chaveCelula(c.coluna, c.linha)),
    caractereEm: (c) => mapa.get(chaveCelula(c.coluna, c.linha))?.caractere,
    palavrasEm: (c) => mapa.get(chaveCelula(c.coluna, c.linha))?.palavras ?? [],
    temOrientacao: (c, orientacao) =>
      mapa.get(chaveCelula(c.coluna, c.linha))?.orientacoes.includes(orientacao) ?? false,
  };
}

/**
 * Textos já colocados no tabuleiro (R5-15).
 *
 * Base e ponte entram no mesmo conjunto: a regra é sobre o tabuleiro, não
 * sobre quem escreveu — repetir uma palavra que o próprio jogo sugeriu é tão
 * repetição quanto repetir a sua.
 *
 * A comparação é do texto canônico, e não da forma sem acento: §4.3 preserva
 * diacríticos, e `SABIA`, `SABIÁ` e `SÁBIA` são três palavras diferentes deste
 * jogo. Tratá-las como uma só proibiria duas jogadas legítimas.
 */
export function palavrasUsadas(cadeia: Cadeia): ReadonlySet<PalavraCanonica> {
  return new Set(cadeia.palavras.map((p) => p.texto));
}

/** Cadeia vazia — estado inicial de T01. */
export const CADEIA_VAZIA: Cadeia = { palavras: [] };

export function adicionarNaCadeia(cadeia: Cadeia, palavra: PalavraColocada): Cadeia {
  return { palavras: [...cadeia.palavras, palavra] };
}

/** Remove as `quantidade` últimas palavras. Base do desfazer (§11). */
export function removerDaCadeia(cadeia: Cadeia, quantidade: number): Cadeia {
  if (quantidade <= 0) return cadeia;
  return { palavras: cadeia.palavras.slice(0, Math.max(0, cadeia.palavras.length - quantidade)) };
}

export function ultimaPalavra(cadeia: Cadeia): PalavraColocada | undefined {
  return cadeia.palavras[cadeia.palavras.length - 1];
}

export function ultimaPonte(cadeia: Cadeia): PalavraColocada | undefined {
  for (let i = cadeia.palavras.length - 1; i >= 0; i -= 1) {
    const p = cadeia.palavras[i];
    if (p !== undefined && p.papel === 'ponte') return p;
  }
  return undefined;
}

export function buscarPalavra(cadeia: Cadeia, id: IdPalavra): PalavraColocada | undefined {
  return cadeia.palavras.find((p) => p.id === id);
}

/** Id determinístico: derivado da posição na cadeia, não de um contador global. */
export function idPalavra(turno: number, orientacao: Orientacao): IdPalavra {
  return `p${turno}${orientacao === 'horizontal' ? 'h' : 'v'}`;
}
